import React, { memo, useCallback, useMemo } from 'react';
import { Icon } from '@Sage/hoi-poi-ui';

import PopoverHoi from 'components/PopoverHoi';
import Select from 'containers/components/Fields/Select';

import './styles.scss';

const EMPTY_OPTIONS = [];

const KpiIdField = memo(({ changeField, data, errors, field, getError }) => {
    const { id, inputAttrs, hidden, label, mandatory, readOnly } = field;
    const options = inputAttrs?.options || EMPTY_OPTIONS;

    const value = useMemo(() => {
        const kpiId = data.kpiId;
        if (!kpiId) return undefined;

        // If already object with value/label, use it
        if (typeof kpiId === 'object' && kpiId.value) {
            return kpiId;
        }

        // Find option to get label
        const option = options.find((opt) => opt.value === kpiId || opt.id === kpiId);
        if (option) {
            return { value: option.value || option.id, label: option.label };
        }

        return { value: kpiId, label: kpiId };
    }, [data.kpiId, options]);

    // Get description for tooltip
    const tooltipContent = useMemo(() => {
        if (!value) return null;

        const selectedOption = options.find(
            (opt) => opt.value === value.value || opt.id === value.value,
        );

        return selectedOption?.description || value.label;
    }, [options, value]);

    const error = useMemo(() => getError(errors[id]), [errors, getError, id]);

    const handleChange = useCallback(
        (newValue) => {
            changeField(id)(newValue?.value || null);
        },
        [changeField, id],
    );

    const selectProps = useMemo(
        () => ({
            error,
            isClearable: false,
            label,
            labelMode: 'horizontal',
            mandatory,
            onChange: handleChange,
            options,
            readOnly,
            value,
        }),
        [error, handleChange, label, mandatory, options, readOnly, value],
    );

    if (hidden) return null;

    return (
        <div className="fm-kpi-id-field">
            <Select {...selectProps} />
            {value && tooltipContent && !readOnly && (
                <div className="fm-kpi-id-field__info">
                    <PopoverHoi placement="top" content={tooltipContent} trigger={['hover']}>
                        <span className="fm-kpi-id-field__info-icon">
                            <Icon name="info" size="small" />
                        </span>
                    </PopoverHoi>
                </div>
            )}
        </div>
    );
});

KpiIdField.displayName = 'KpiIdField';

export default KpiIdField;
